import React from 'react';
import PropTypes from 'prop-types';
import { ThinkingOrb } from 'thinking-orbs';

// The app's loading mark. One wrapper so every loading surface animates the
// same way and picks up the same tint rules; swapping the look later is a
// one-file change.
//
// The orb draws on a 2D canvas (no WebGL, no dependencies, ~8KB gzipped) and
// renders a static frame when the visitor asks for reduced motion, so it is
// safe to use everywhere the old CSS spinners were.

// tintFor keeps the orb legible on whatever it sits on. An accent equal to the
// ink would vanish against its own text (templates 9 and 11 do exactly that —
// cartKit and leadWheel guard the same way), and a colour we cannot parse is
// worse than none: the library's own grayscale ink is tuned to read on both
// light and dark substrates.
export const tintFor = ({ accent, ink } = {}) => {
  const c = String(accent ?? '').trim();
  if (!c || c === String(ink ?? '').trim()) return undefined;
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c) || /^rgb\(/i.test(c) ? c : undefined;
};

// Screen-reader-only, inline. This used to be Tabler's `.visually-hidden`
// class — but store domains no longer load Tabler, and with the class
// undefined the label painted as ordinary text beside the visible one:
// "Loading storefront" twice. The mark renders on both hosts, so it carries
// its own styling and depends on no stylesheet.
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
};

const Orb = ({ size = 64, label, color, dark, className, style, state = 'breathing' }) => (
  <span className={className} style={{ display: 'inline-flex', lineHeight: 0, ...style }}>
    <ThinkingOrb
      state={state}
      size={size}
      // `auto` lets the library read a data-theme/.dark ancestor, then the
      // visitor's OS preference — the right default when the page's own
      // palette is not known yet (it is still loading, after all).
      theme={dark === undefined ? 'auto' : dark ? 'dark' : 'light'}
      color={color}
      aria-hidden="true"
    />
    {label ? <span style={SR_ONLY}>{label}</span> : null}
  </span>
);

Orb.propTypes = {
  state: PropTypes.string,
  size: PropTypes.oneOf([64, 32, 20]),
  label: PropTypes.string,
  color: PropTypes.string,
  dark: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

// The pill: orb on the left, the text beside it, inside a soft rounded
// capsule. Every loading surface uses this, so the look is one place.
// Styled inline: store domains do not load the back-office stylesheet.
export const pillStyle = (dark) => ({
  display: 'inline-flex', alignItems: 'center', gap: 12,
  padding: '9px 22px 9px 10px', borderRadius: 999,
  background: dark ? 'rgba(255,255,255,.035)' : '#fff',
  border: `1px solid ${dark ? 'rgba(255,255,255,.07)' : '#E6E3DB'}`,
  boxShadow: dark ? 'none' : '0 1px 2px rgba(21,24,26,.04), 0 10px 24px -16px rgba(21,24,26,.25)',
  color: dark ? '#a1a1a6' : '#6B6F72',
  fontSize: 15, fontWeight: 500, letterSpacing: '.01em', lineHeight: 1.2,
  fontFamily: "Manrope, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  whiteSpace: 'nowrap', maxWidth: '100%',
});

export const OrbPill = ({ label = 'Loading', state = 'breathing', color, dark = false, ink, style }) => (
  <span role="status" aria-live="polite" style={{ ...pillStyle(dark), ...(ink ? { color: ink } : null), ...style }}>
    <Orb size={32} state={state} color={color} dark={dark} />
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
  </span>
);

OrbPill.propTypes = {
  label: PropTypes.string,
  state: PropTypes.string,
  color: PropTypes.string,
  dark: PropTypes.bool,
  ink: PropTypes.string,
  style: PropTypes.object,
};

export default Orb;
